import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import { teamApi } from '@/lib/api';

const LOGO_URL = "https://customer-assets.emergentagent.com/job_procurement-central-1/artifacts/itphun63_FullLogo_NoBuffer%20%281%29.png";

export default function Register() {
  const navigate = useNavigate();
  const { register } = useAuth();
  const [loading, setLoading] = useState(false);
  const [checking, setChecking] = useState(true);
  const [hasUsers, setHasUsers] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    password: '',
    confirmPassword: '',
  });

  useEffect(() => {
    // Check if users already exist
    teamApi.getMembers()
      .then(res => {
        if (res.data.length > 0) {
          setHasUsers(true);
        }
      })
      .catch(() => {
        // Ignore - probably unauthorized which means users exist
        setHasUsers(true);
      })
      .finally(() => setChecking(false));
    // Check runs once on mount
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (formData.password !== formData.confirmPassword) {
      toast.error('Passwords do not match');
      return;
    }
    
    if (formData.password.length < 6) {
      toast.error('Password must be at least 6 characters');
      return;
    }

    setLoading(true);
    try {
      await register(formData.email, formData.password, formData.name);
      toast.success('Account created successfully!');
      navigate('/');
    } catch (error) {
      console.error('Registration error:', error);
      toast.error(error.response?.data?.detail || 'Registration failed');
    } finally {
      setLoading(false);
    }
  };

  if (checking) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-pulse text-muted-foreground">Loading...</div>
      </div>
    );
  }

  if (hasUsers) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background p-4">
        <Card className="w-full max-w-md border border-border shadow-none rounded-sm">
          <CardHeader className="text-center pb-2">
            <img src={LOGO_URL} alt="Solar Paradigm" className="h-12 mx-auto mb-4" />
            <CardTitle className="text-xl font-heading">Registration Closed</CardTitle>
            <CardDescription>
              An admin account already exists. Ask your admin to send you an invite link to join the team.
            </CardDescription>
          </CardHeader>
          <CardContent className="text-center">
            <Link to="/login">
              <Button className="rounded-sm">Go to Login</Button>
            </Link>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-background p-4" data-testid="register-page">
      <Card className="w-full max-w-md border border-border shadow-none rounded-sm">
        <CardHeader className="text-center pb-2">
          <img src={LOGO_URL} alt="Solar Paradigm" className="h-12 mx-auto mb-4" />
          <CardTitle className="text-xl font-heading">Create Admin Account</CardTitle>
          <CardDescription>Set up your organization's admin account</CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <Label htmlFor="name">Full Name</Label>
              <Input
                id="name"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                className="rounded-sm mt-1.5"
                required
                data-testid="input-register-name"
              />
            </div>
            <div>
              <Label htmlFor="email">Email</Label>
              <Input
                id="email"
                type="email"
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                className="rounded-sm mt-1.5"
                required
                data-testid="input-register-email"
              />
            </div>
            <div>
              <Label htmlFor="password">Password</Label>
              <Input
                id="password"
                type="password"
                value={formData.password}
                onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                className="rounded-sm mt-1.5"
                required
                data-testid="input-register-password"
              />
            </div>
            <div>
              <Label htmlFor="confirmPassword">Confirm Password</Label>
              <Input
                id="confirmPassword"
                type="password"
                value={formData.confirmPassword}
                onChange={(e) => setFormData({ ...formData, confirmPassword: e.target.value })}
                className="rounded-sm mt-1.5"
                required
                data-testid="input-register-confirm"
              />
            </div>
            <Button 
              type="submit" 
              className="w-full rounded-sm" 
              disabled={loading}
              data-testid="btn-register"
            >
              {loading ? 'Creating Account...' : 'Create Account'}
            </Button>
          </form>
          <p className="text-center text-sm text-muted-foreground mt-4">
            Already have an account?{' '}
            <Link to="/login" className="text-primary hover:underline">
              Sign in
            </Link>
          </p>
        </CardContent>
      </Card>
    </div>
  );
}
